import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  IInventorySellerProduct,
  IProfileEntity,
} from '@boma/supplier-portal/shared/entities';
import {
  InventoryService,
  PricingService,
  SearchService,
  StockService,
} from '@boma/supplier-portal/shared/services';
import { getSelectedOrganizationInfo } from '@boma/supplier-portal/shared/state';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { SearchMode } from '@boma/client-shared';
import { ConfirmationService, SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';

@Component({
  selector: 'boma-product-selector-table',
  templateUrl: './product-selector-table.component.html',
  styleUrls: ['./product-selector-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ProductSelectorTableComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() showPrices = false;
  @Input() searchMode = false; // toggle which API call we will be using
  @Input() selectedLocation;
  @Input() allLocationProducts = false;
  @Input() showQTYInput = true;
  @Input() initialProducts: IInventorySellerProduct[] | null = null;
  @Input() showOverView = true;
  @Input() showCompleteButton = false;
  @Input() submitting = false;
  @Input() showStockLocations = true;
  @Input() showQty = true;
  @Input() totalQtyOnly = false;
  @Input() checkoutButtonText = 'Checkout';

  @Input() returnZeroQTY = false;
  @Input() customerAndLocation = null;
  @Input() hideZeroStocks = true;

  @Output() productsChanged = new EventEmitter<IInventorySellerProduct[]>();
  @Output() completeProcess = new EventEmitter();
  @Output() productPagination = new EventEmitter();

  products: IInventorySellerProduct[] = [];

  // These any's will make things difficult for onboarding new developers
  newProductList = [];
  locations = [];

  selectedItems = [];

  totalItems = 0;

  loading = true;

  perPage = 250;

  clonedProducts: { [s: string]: any } = {};

  totalZeroStocks = 0;

  totalHeaders = null;

  filterTerm = '';

  salesInfo = {
    subTotal: 0,
    discountTotal: 0,
    taxTotal: 0,
    grandTotal: 0,
  };

  // Component code
  currentPageReportTemplate: string;

  selectedSupplyingLocation: any;
  private currentEntity: IProfileEntity;
  private entitySub: Subscription;

  constructor(
    private inventoryService: InventoryService,
    private store: Store,
    private searchService: SearchService,
    private confirmationService: ConfirmationService,
    private pricingService: PricingService,
    private stockService: StockService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedLocation) {
      this.searchProducts();
    }

    if (changes.customerAndLocation) {
      this.getPricesForCustomerLocation();
    }
  }

  ngOnInit(): void {
    if (this.initialProducts) {
      this.products = JSON.parse(JSON.stringify(this.initialProducts)); // remove ref to immutable array
    }

    this.entitySub = this.store
      .select(getSelectedOrganizationInfo)
      .subscribe((org) => {
        this.currentEntity = org;
        this.searchProducts();
      });
  }

  searchProducts() {
    if (!this.currentEntity?.id || !this.selectedLocation) return;
    this.loading = true;

    if (this.allLocationProducts) {
      this.getAllEnabledProductsOnLocation();
    } else if (this.searchMode) {
      this.searchProductsForLocation();
    } else {
      this.updateProductsForLocation();
    }
  }

  getPricesForCustomerLocation(productIds = []) {
    if (
      !this.currentEntity?.id ||
      !this.customerAndLocation?.entityId ||
      !this.customerAndLocation?.locationId ||
      this.loading
    ) {
      return;
    }

    this.loading = true;
    // this.customerAndLocation = {
    //   entityId: this.customer.id,
    //   locationId: this.receivingLocation.id,
    // };

    // Update and fire off to get the customers custom pricing for this location.

    if (!productIds) {
      productIds = this.newProductList.map((x) => x.id);
    }

    // We need to match this up with the products currently loaded.
    this.pricingService
      .getCustomerLocationPricing(
        this.currentEntity.id,
        this.customerAndLocation.entityId,
        this.customerAndLocation.locationId,
        {
          productIds: productIds,
        }
      )
      .subscribe((x) => {
        //Now that we have the customers pricing we need to make sure to update the pricing on the table.
        x.items.map((x) => {
          const productToUpdate = this.newProductList.find(
            (a) => a.id === x.product.id
          );
          if (productToUpdate) {
            // productToUpdate.price = 99.99;

            productToUpdate.customPricing = {
              tiers: x.pricingTiers.sort((a, b) => a.price - b.price),
              location: x.locationPricing,
            };

            // Lets Nullify Location Pricing if the user has a custom price that has no group name.
            const customerTiers = productToUpdate.customPricing.tiers.filter(
              (tier) => !tier.pricingGroupName
            );

            productToUpdate.cheapestTier = null;
            if (productToUpdate.customPricing.tiers?.length) {
            }

            productToUpdate.cheapestTier =
              productToUpdate.customPricing.tiers[0];

            if (customerTiers.length > 0) {
              productToUpdate.hasCustomerTier = true;
              productToUpdate.customPricing.location = null; // Remove location pricing if customer tier exists
            }

            // find the lowest tier available to customer

            //lets get the min  max
            productToUpdate.lowestMinimumQty = null;
            productToUpdate.highestMaximumQty = null;
            if (x.pricingTiers?.length) {
              const minimumOrderQuantities = x.pricingTiers.map(
                (tier) => tier.minimumOrderQuantity
              );
              const maximumOrderQuantities = x.pricingTiers.map(
                (tier) => tier.maximumOrderQuantity
              );

              const minQty = Math.min(...minimumOrderQuantities);
              const maxQty = Math.max(...maximumOrderQuantities);

              productToUpdate.lowestMinimumQty = isFinite(minQty)
                ? minQty
                : null;
              productToUpdate.highestMaximumQty = isFinite(maxQty)
                ? maxQty
                : null;
            }
          } else {
            // Product with the specified ID was not found in the array
          }
        });

        //Need to disable any of the products that did not return and state that they are not available as none of the suppliers locations distribute to the selected location of the customer

        this.newProductList.forEach((product) => {
          const foundItem = x.items.find(
            (item) => item.product.id === product.id
          );
          if (!foundItem) {
            product.error =
              'No distribution available to customer location, adjust your distribution boundaries.';
          }
        });

        this.loading = false;
      });
  }

  onSelectedTier(rowData, tier) {
    rowData.selectedTier = tier;
    rowData.qty = tier.minimumOrderQuantity;
    rowData.newPrice = tier.price;

    this.updateSelectedProductList(rowData);
  }

  onRowChange(product) {
    product.selectedTier = null;
    // we need to make sure we update the price based on the users pricing tiers
    if (product?.customPricing?.tiers.length) {
      const qty = product.qty;
      var lowestPrice = null;

      product.customPricing.tiers.forEach(function (tier) {
        if (
          qty >= tier.minimumOrderQuantity &&
          qty <= tier.maximumOrderQuantity
        ) {
          // we have a qty that matches lets add a highlight class?
          if (lowestPrice == null || tier.price < lowestPrice) {
            lowestPrice = tier.price;
            product.selectedTier = tier;
            // TODO: I think we should should update the comment to include the pricing tier at this point.
          }
        }
      });
      product.newPrice = lowestPrice;
    }

    this.updateSelectedProductList(product);
  }

  //TODO when updating the product we need to check pricing tiers and other promotions to ensure we bill the right value
  updateSelectedProductList(product) {
    if (
      product.qty >= 1 ||
      (this.returnZeroQTY && (product.qty === null || product.qty === 0))
    ) {
      product.qty = product.qty ? product.qty : 0;
      this.clonedProducts[product.id] = { ...product };
    } else {
      delete this.clonedProducts[product.id];
    }

    this.salesInfo = Object.values(this.clonedProducts).reduce(
      (info, value) => {
        if (!value.qty) return info;

        const normalPrice = value.price;
        const itemQuantity = value.qty;
        const paidPrice =
          value?.newPrice !== null &&
          value?.newPrice == 0 &&
          value.free === true
            ? 0
            : value?.newPrice !== null && value?.newPrice > 0
            ? value.newPrice
            : value.price;

        const itemDiscount =
          paidPrice === 0 && value?.free === true
            ? normalPrice
            : paidPrice < normalPrice && paidPrice > 0
            ? normalPrice - paidPrice
            : 0;

        const priceBeforeTax = paidPrice / (value.taxRate / 100 + 1);

        info.subTotal += value.price * itemQuantity;
        info.discountTotal += itemDiscount * itemQuantity * -1;
        info.taxTotal += (paidPrice - priceBeforeTax) * itemQuantity;
        info.grandTotal += paidPrice * itemQuantity;

        return info;
      },
      {
        subTotal: 0,
        discountTotal: 0,
        taxTotal: 0,
        grandTotal: 0,
      }
    );

    this.selectedItems = [];
    this.totalItems = 0;

    this.selectedItems = Object.values(this.clonedProducts)
      .map((value) => {
        if (!value.qty) return null;

        const normalPrice = value.price;
        const itemQuantity = value.qty;
        const paidPrice =
          value?.newPrice !== null &&
          value?.newPrice == 0 &&
          value.free === true
            ? 0
            : value?.newPrice !== null && value?.newPrice > 0
            ? value.newPrice
            : value.price;
        const itemDiscount =
          paidPrice === 0 && value?.free === true
            ? normalPrice
            : paidPrice < normalPrice && paidPrice > 0
            ? normalPrice - paidPrice
            : 0;
        const priceBeforeTax = paidPrice / (value.taxRate / 100 + 1);

        return {
          name: value.name,
          productId: value.id,
          units: itemQuantity,
          price: normalPrice,
          discount: itemDiscount,
          paidPrice: paidPrice,
          taxRate: value?.taxRate,
          pricingTier: value?.selectedTier || null,
        };
      })
      .filter((item) => item !== null);

    this.totalItems = this.selectedItems.reduce(
      (total, item) => total + item.units,
      0
    );

    this.productsChanged.emit(this.selectedItems);
  }

  searchProductsForLocation() {
    if (!this.currentEntity?.id || !this.selectedLocation) {
      return;
    }
    this.loading = true;

    if (!this.currentEntity?.id) return;
    this.searchService
      .searchProducts(
        this.currentEntity.id,
        '',
        '1',
        '250', // TODO make dynamic controls
        [],
        [],
        this.selectedLocation,
        null,
        null,
        SearchMode.AllEntityCatalogueProducts,
        false
      )
      .subscribe((x) => {
        this.loading = false;
        this.newProductList = x.items;
        this.hideZeroStocks = false;

        //sort by stock... TODO the requests keep changing on how to handle this so we need to modify the api to accommodate sort logic
        // this.newProductList.sort(
        //   (a, b) => b.remainingStockCount - a.remainingStockCount
        // );

        // SEARCH FOR CUSTOMER SPECIFIC PRICING
        const theseProductIds = this.newProductList.map((x) => x.id);

        this.getPricesForCustomerLocation(theseProductIds);
      });
  }

  getAllEnabledProductsOnLocation() {
    if (!this.currentEntity?.id || !this.selectedLocation) {
      return;
    }
    this.loading = true;
    if (!this.currentEntity?.id) return;
    this.stockService
      .getAllStocksAndEnabledProducts(
        this.currentEntity.id,
        [this.selectedLocation],
        null,
        null,
        '1',
        '999' // TODO make dynamic controls
      )
      .subscribe((x) => {
        this.loading = false;
        this.newProductList = x.items.map((x) => ({
          ...x.locationProductStocks[0],
          ...x.product,
        }));

        this.hideZeroStocks = false;

        //sort by stock... TODO the requests keep changing on how to handle this so we need to modify the api to accommodate sort logic
        // this.newProductList.sort(
        //   (a, b) => b.remainingStockCount - a.remainingStockCount
        // );

        // SEARCH FOR CUSTOMER SPECIFIC PRICING
        const theseProductIds = this.newProductList.map((x) => x.id);

        this.getPricesForCustomerLocation(theseProductIds);
      });
  }

  customSort(event: SortEvent) {
    event.data.sort((data1, data2) => {
      let value1 = data1[event.field];
      let value2 = data2[event.field];
      let result = null;

      if (value1 == null && value2 != null) result = -1;
      else if (value1 != null && value2 == null) result = 1;
      else if (value1 == null && value2 == null) result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
        result = value1.localeCompare(value2);
      else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;

      return event.order * result;
    });
  }

  updateProductsForLocation() {
    if (!this.currentEntity?.id || !this.selectedLocation) {
      return;
    }
    if (!this.currentEntity?.id) return;
    this.loading = true;
    this.inventoryService
      .getStockLevels(
        this.currentEntity.id,
        this.selectedLocation,
        '',
        '1',
        this.perPage.toString()
      )
      .subscribe((i) => {
        this.newProductList = [];
        this.loading = false;
        i.items.map((x) => {
          x.product.stockLocation = x.locationProductStocks[0]
            ?.stockLocationName
            ? x.locationProductStocks[0]?.stockLocationName
            : x.locationProductStocks[0]?.locationName;
          x.product.stockLocationName =
            x.locationProductStocks[0]?.stockLocationName;
          x.product.reservedStockCountStockLocation =
            x.locationProductStocks[0]?.stockLocationReservedStockCount;
          x.product.remainingStockCountStockLocation =
            x.locationProductStocks[0]?.stockLocationRemainingStockCount;
          x.product.reservedStockCount =
            x.locationProductStocks[0]?.reservedStockCount;
          x.product.remainingStockCount =
            x.locationProductStocks[0]?.remainingStockCount;
          x.product.price = x.locationProductStocks[0]?.price;
          x.product.cost = x.locationProductStocks[0]?.cost;
          x.product.taxRate = x.locationProductStocks[0]?.taxRate;
          x.product.free = false;
          this.newProductList.push(x.product);
        });

        // this.locations = this.newProductList.map((x) => {
        //   return { name: x.stockLocation };
        // });

        if (this.returnZeroQTY) this.productPagination.emit(i);

        this.newProductList.sort(
          (a, b) => b.remainingStockCount - a.remainingStockCount
        );

        this.totalZeroStocks = this.newProductList.filter(
          (x) => x.remainingStockCount === 0
        ).length;

        // SEARCH FOR CUSTOMER SPECIFIC PRICING
        const theseProductIds = this.newProductList.map((x) => x.id);

        this.getPricesForCustomerLocation(theseProductIds);
      });
  }

  confirm() {
    const freeItems = this.selectedItems.filter(
      (x) => x.discount === x.price
    ).length;

    let confirmationMessage = '';

    if (this.salesInfo.grandTotal <= 0) {
      confirmationMessage =
        'You are about to check out with a zero cart amount, this transaction will be scrutinised. Are you sure you want to do this?';
    } else if (freeItems > 0) {
      confirmationMessage =
        freeItems +
        ' item(s) in this transaction were discounted 100%, and set to free, are you sure you want to continue?';
    } else if (
      ((this.salesInfo.subTotal - this.salesInfo.grandTotal) /
        this.salesInfo.subTotal) *
        100 >
      59
    ) {
      confirmationMessage =
        'The transaction has a total discount value of larger than 60%, this transaction will be scrutinised. Is this correct?';
    }

    if (confirmationMessage !== '') {
      this.confirmationService.confirm({
        header: 'Warning!',
        icon: 'pi pi-exclamation-triangle',
        message: confirmationMessage,
        accept: () => {
          this.completeTheSale();
        },
        reject: () => {
          return;
        },
      });
    } else {
      this.completeTheSale();
    }
  }

  completeTheSale() {
    // Emit to the parent
    this.completeProcess.emit();
  }

  clear(table: Table) {
    this.filterTerm = '';
    table.clear();
  }

  clearSale() {
    //Clear all QTY and New Price Data
    this.newProductList.map((x) => {
      x.newPrice = null;
      x.qty = 0;
    });

    this.selectedItems = [];
    this.productsChanged.emit(this.selectedItems);
  }

  ngOnDestroy(): void {
    this.entitySub?.unsubscribe();
  }
}
